/**
 * Base64 Type Detector
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { ITypeDetector } from '@/interfaces/ITypeDetector';

export const Base64Detector: ITypeDetector = {
  id: 'base64-detector',
  name: 'Base64 Detector',
  priority: 50,
  detectedType: SEMANTIC_TYPES.BASE64,

  detect(input: string): ISemanticType | null {
    const trimmed = input.trim();
    
    // Must be reasonable length and not look like plain text
    if (trimmed.length < 4) {
      return null;
    }

    // Standard Base64 character set (with optional padding)
    const base64Regex = /^[A-Za-z0-9+/]+={0,2}$/;
    
    // Must match Base64 pattern
    if (!base64Regex.test(trimmed)) {
      return null;
    }

    // Length should be divisible by 4 (after padding)
    const paddedLength = trimmed.length + (4 - (trimmed.length % 4)) % 4;
    if (paddedLength % 4 !== 0) {
      return null;
    }

    // Try to decode it
    try {
      const decoded = atob(trimmed);
      
      // If it decodes to mostly printable ASCII, it's likely Base64
      // (but could also just be plain text that happens to match)
      const printableRatio = decoded.split('').filter(c => {
        const code = c.charCodeAt(0);
        return code >= 32 && code < 127;
      }).length / decoded.length;

      // If more than 80% printable and contains non-Base64 characters in decoded form,
      // it's more likely to be actual Base64-encoded content
      if (printableRatio > 0.8 && decoded !== trimmed) {
        return SEMANTIC_TYPES.BASE64;
      }

      // Also accept if it's clearly binary (low printable ratio)
      if (printableRatio < 0.5) {
        return SEMANTIC_TYPES.BASE64;
      }
    } catch {
      return null;
    }

    return null;
  },
};
